import { useCallback, useEffect, useState } from 'react';
import { listCustomerSubscriptions } from '../services/esetApi';
import type { EsetSubscriptionsList } from '../services/esetApi';

interface UseEsetSubscriptionsResult {
  data: EsetSubscriptionsList | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * A customer's ESET licenses (from the sm4rt-eset-integration admin API). Only
 * fetches while `enabled` (the manage panel is open) and the customer GUID is set.
 */
export function useEsetSubscriptions(
  customerGuid: string | null,
  enabled: boolean,
): UseEsetSubscriptionsResult {
  const [data, setData] = useState<EsetSubscriptionsList | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!customerGuid || !enabled) {
        setData(null);
        setError(null);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await listCustomerSubscriptions(customerGuid, signal);
        if (!signal?.aborted) setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load ESET licenses');
        setData(null);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [customerGuid, enabled],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return { data, isLoading, error, refresh: () => load() };
}
